import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Assessment,
  Download,
  Print,
  FilterList,
  DateRange,
  TrendingUp,
  AttachMoney,
  Inventory,
  PieChart,
  BarChart,
  Refresh,
  FileDownload,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
} from 'recharts';
import axios from 'axios';

const Reports = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reportType, setReportType] = useState('summary');
  const [dateRange, setDateRange] = useState('last30days');
  const [reportData, setReportData] = useState({
    summary: {},
    predictions: [],
    revenue: [],
    inventory: [],
    costs: []
  });

  useEffect(() => {
    generateReport();
  }, [reportType, dateRange]);

  const generateReport = async () => {
    try {
      setLoading(true);
      
      // Fetch data from multiple endpoints
      const [predictionsRes, estimationsRes, inventoryRes] = await Promise.all([
        axios.get('/api/get-predictions/'),
        axios.get('/api/estimations/'),
        axios.get('/api/add-batch/')
      ]);

      const predictions = predictionsRes.data.predictions || [];
      const estimations = estimationsRes.data.estimations || [];
      const inventory = inventoryRes.data.inventory || [];

      // Process data for reports
      const processedData = processReportData(predictions, estimations, inventory);
      setReportData(processedData);
      setError('');
    } catch (error) {
      console.error('Error generating report:', error);
      setError('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const processReportData = (predictions, estimations, inventory) => {
    // Calculate summary metrics
    const totalPredictions = predictions.length;
    const totalRevenue = estimations.reduce((sum, est) => sum + parseFloat(est.total_estimation || 0), 0);
    const totalCosts = estimations.reduce((sum, est) => sum + parseFloat(est.amount_taken || 0), 0);
    const totalProfit = totalRevenue - totalCosts;

    // Process revenue trends
    const revenueTrends = estimations.slice(-12).map((est, index) => ({
      month: `Month ${index + 1}`,
      revenue: parseFloat(est.total_estimation || 0),
      costs: parseFloat(est.amount_taken || 0),
      profit: parseFloat(est.total_estimation || 0) - parseFloat(est.amount_taken || 0)
    }));

    // Process menu popularity
    const menuStats = predictions.reduce((acc, pred) => {
      const menu = pred.menu_name;
      if (acc[menu]) {
        acc[menu].count += 1;
        acc[menu].totalGuests += pred.guests;
      } else {
        acc[menu] = { count: 1, totalGuests: pred.guests };
      }
      return acc;
    }, {});

    const menuPopularity = Object.entries(menuStats).map(([name, stats]) => ({
      name,
      orders: stats.count,
      averageGuests: Math.round(stats.totalGuests / stats.count)
    }));

    // Process inventory status
    const inventoryByStatus = inventory.reduce((acc, item) => {
      const status = item.status || 'Fresh';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    const inventoryChart = Object.entries(inventoryByStatus).map(([status, count]) => ({
      name: status,
      value: count
    }));

    return {
      summary: {
        totalPredictions,
        totalRevenue: totalRevenue.toFixed(2),
        totalCosts: totalCosts.toFixed(2),
        totalProfit: totalProfit.toFixed(2),
        profitMargin: totalRevenue > 0 ? ((totalProfit / totalRevenue) * 100).toFixed(1) : 0
      },
      predictions,
      revenue: revenueTrends,
      menuPopularity,
      inventory: inventoryChart,
      costs: estimations
    };
  };

  const downloadReport = (format) => {
    // In a real app, this would generate and download the report
    const reportContent = JSON.stringify(reportData, null, 2);
    const blob = new Blob([reportContent], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report_${reportType}_${new Date().toISOString().split('T')[0]}.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const printReport = () => {
    window.print();
  };

  const COLORS = ['#1976d2', '#42a5f5', '#64b5f6', '#90caf9', '#bbdefb'];

  const SummaryReport = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={3}>
        <Card className="card-hover">
          <CardContent sx={{ textAlign: 'center' }}>
            <Assessment sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
              {reportData.summary.totalPredictions}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Predictions
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} md={3}>
        <Card className="card-hover">
          <CardContent sx={{ textAlign: 'center' }}>
            <AttachMoney sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
              PKR {reportData.summary.totalRevenue}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Revenue
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={3}>
        <Card className="card-hover">
          <CardContent sx={{ textAlign: 'center' }}>
            <TrendingUp sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main' }}>
              PKR {reportData.summary.totalProfit}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Profit
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={3}>
        <Card className="card-hover">
          <CardContent sx={{ textAlign: 'center' }}>
            <PieChart sx={{ fontSize: 48, color: 'info.main', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'info.main' }}>
              {reportData.summary.profitMargin}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Profit Margin
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Revenue Trends Chart */}
      <Grid item xs={12} md={8}>
        <Card className="card-hover">
          <CardContent>
            <Typography variant="h6" gutterBottom>Revenue Trends</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={reportData.revenue}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Area type="monotone" dataKey="revenue" stackId="1" stroke="#1976d2" fill="#1976d2" fillOpacity={0.6} />
                <Area type="monotone" dataKey="costs" stackId="2" stroke="#f57c00" fill="#f57c00" fillOpacity={0.6} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>

      {/* Menu Popularity */}
      <Grid item xs={12} md={4}>
        <Card className="card-hover">
          <CardContent>
            <Typography variant="h6" gutterBottom>Menu Popularity</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <RechartsPieChart>
                <Pie
                  data={reportData.menuPopularity}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="orders"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {reportData.menuPopularity.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip />
              </RechartsPieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const PredictionsReport = () => (
    <Card className="card-hover">
      <CardContent>
        <Typography variant="h6" gutterBottom>Predictions Report</Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Location</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Menu</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Guests</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Items</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportData.predictions.map((prediction, index) => (
                <TableRow key={prediction.id} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}>
                  <TableCell>{new Date(prediction.timestamp).toLocaleDateString()}</TableCell>
                  <TableCell>{prediction.location}</TableCell>
                  <TableCell>
                    <Chip label={prediction.menu_name} size="small" color="primary" />
                  </TableCell>
                  <TableCell>{prediction.guests}</TableCell>
                  <TableCell>{Object.keys(prediction.menu_items || {}).length}</TableCell>
                  <TableCell>
                    <Chip label="Completed" size="small" color="success" variant="outlined" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );

  const renderReport = () => {
    switch (reportType) {
      case 'summary':
        return <SummaryReport />;
      case 'predictions':
        return <PredictionsReport />;
      case 'revenue':
        return <SummaryReport />; // For now, showing summary for revenue
      default:
        return <SummaryReport />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}
          >
            <Assessment sx={{ fontSize: 'inherit', color: '#1976d2' }} />
            Business Reports
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh data">
              <IconButton onClick={generateReport} color="primary">
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<Print />}
              onClick={printReport}
              size="small"
            >
              Print
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={() => downloadReport('json')}
              size="small"
            >
              Export
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Card className="card-hover" sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Report Type</InputLabel>
                  <Select
                    value={reportType}
                    label="Report Type"
                    onChange={(e) => setReportType(e.target.value)}
                  >
                    <MenuItem value="summary">Summary Dashboard</MenuItem>
                    <MenuItem value="predictions">Predictions Report</MenuItem>
                    <MenuItem value="revenue">Revenue Analysis</MenuItem>
                    <MenuItem value="inventory">Inventory Status</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Date Range</InputLabel>
                  <Select
                    value={dateRange}
                    label="Date Range"
                    onChange={(e) => setDateRange(e.target.value)}
                  >
                    <MenuItem value="last7days">Last 7 Days</MenuItem>
                    <MenuItem value="last30days">Last 30 Days</MenuItem>
                    <MenuItem value="last90days">Last 90 Days</MenuItem>
                    <MenuItem value="thisyear">This Year</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  startIcon={<FilterList />}
                  onClick={generateReport}
                  fullWidth
                  disabled={loading}
                >
                  Generate Report
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </motion.div>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Report Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        {renderReport()}
      </motion.div>
    </Box>
  );
};

export default Reports; 